import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import { PageLoader, ErrorState, EmptyState, Field, StatusBadge } from '../../components/ui';
import { eventsApi, recapsApi, mediaApi } from '../../api/resources';
import { assetUrl } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import { formatDate } from '../../lib/format';

const EMPTY_FORM = { id: null, title: '', body: '', status: 'draft', media: [] };

export default function AdminEventRecaps() {
  const { slug } = useParams();
  const toast = useToast();

  const [event, setEvent] = useState(null);
  const [state, setState] = useState({ loading: true, error: null, items: [] });
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const loadEvent = useCallback(() => {
    eventsApi.get(slug).then(setEvent).catch(() => setEvent(null));
  }, [slug]);

  const loadRecaps = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: null }));
    recapsApi
      .list(slug, { status: 'all', per_page: 100 })
      .then((res) => setState({ loading: false, error: null, items: res.items }))
      .catch((error) => setState({ loading: false, error, items: [] }));
  }, [slug]);

  useEffect(() => {
    loadEvent();
    loadRecaps();
  }, [loadEvent, loadRecaps]);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function resetForm() {
    setForm(EMPTY_FORM);
  }

  function editRecap(recap) {
    setForm({
      id: recap.id,
      title: recap.title || '',
      body: recap.body || '',
      status: recap.status || 'draft',
      media: (recap.media || []).map((m) => ({ id: m.id, url: m.url, alt_text: m.alt_text })),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function handleUpload(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('kind', 'image'); // recap galleries are image-only
        fd.append('alt_text', file.name);
        const media = await mediaApi.upload(fd);
        // Read the usable URL from `media.url` (NOT `media.path` — known past bug).
        const uploaded = { id: media?.id, url: media?.url, alt_text: media?.alt_text || file.name };
        if (!uploaded.id) throw new Error('Upload succeeded but no media id was returned.');
        setForm((f) => ({ ...f, media: [...f.media, uploaded] }));
      }
      toast.success(files.length > 1 ? 'Photos uploaded.' : 'Photo uploaded.');
    } catch (err) {
      toast.error(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
      e.target.value = ''; // allow re-selecting the same file
    }
  }

  function removePhoto(id) {
    setForm((f) => ({ ...f, media: f.media.filter((m) => m.id !== id) }));
  }

  function movePhoto(index, delta) {
    setForm((f) => {
      const next = [...f.media];
      const target = index + delta;
      if (target < 0 || target >= next.length) return f;
      [next[index], next[target]] = [next[target], next[index]];
      return { ...f, media: next };
    });
  }

  /** Persist the form as a recap. Returns the saved recap id, or null on failure. */
  async function persist() {
    if (!form.body.trim()) {
      toast.error('Body is required.');
      return null;
    }
    const payload = {
      title: form.title.trim() || null,
      body: form.body,
      media_ids: form.media.map((m) => m.id),
    };
    if (form.id) {
      const saved = await recapsApi.update(slug, form.id, payload);
      return saved?.id ?? form.id;
    }
    const saved = await recapsApi.create(slug, payload); // always a draft
    return saved?.id ?? null;
  }

  async function handleSaveDraft() {
    setBusy(true);
    try {
      const id = await persist();
      if (id == null) return;
      // Editing a published recap + "Save draft" reverts it to draft.
      if (form.id && form.status === 'published') {
        await recapsApi.update(slug, id, { status: 'draft' });
      }
      toast.success(form.id ? 'Recap saved.' : 'Draft saved.');
      resetForm();
      loadRecaps();
      loadEvent();
    } catch (err) {
      toast.error(err.message || 'Could not save recap.');
    } finally {
      setBusy(false);
    }
  }

  async function handlePublish() {
    setBusy(true);
    try {
      const id = await persist();
      if (id == null) return;
      await recapsApi.publish(slug, id);
      toast.success('Recap published.');
      resetForm();
      loadRecaps();
      loadEvent();
    } catch (err) {
      toast.error(err.message || 'Could not publish recap.');
    } finally {
      setBusy(false);
    }
  }

  async function handlePublishRow(recap) {
    setBusy(true);
    try {
      await recapsApi.publish(slug, recap.id);
      toast.success('Recap published.');
      loadRecaps();
      loadEvent();
    } catch (err) {
      toast.error(err.message || 'Could not publish recap.');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(recap) {
    setBusy(true);
    try {
      await recapsApi.remove(slug, recap.id);
      toast.success('Recap deleted.');
      setConfirmDelete(null);
      if (form.id === recap.id) resetForm();
      loadRecaps();
      loadEvent();
    } catch (err) {
      toast.error(err.message || 'Could not delete recap.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <Seo title="Manage Recaps" />

      <div>
        <Link to="/admin/events" className="text-sm text-ink-muted hover:text-maroon-700">
          ← Back to events
        </Link>
        <div className="mt-2 flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold">Event recaps</h1>
            {event && <p className="mt-1 text-sm text-ink-muted">{event.title}</p>}
          </div>
          {form.id && (
            <button onClick={resetForm} className="btn-ghost text-sm" disabled={busy}>
              + New recap
            </button>
          )}
        </div>
      </div>

      {/* Create / edit panel */}
      <div className="card space-y-5 p-6">
        <h2 className="font-display text-lg font-semibold">
          {form.id ? 'Edit recap' : 'New recap'}
        </h2>

        <Field label="Title" hint="Optional heading, e.g. “Day 1 highlights”.">
          <input
            className="input"
            value={form.title}
            onChange={(e) => update('title', e.target.value)}
            placeholder="Optional title"
          />
        </Field>

        <Field label="Body *" hint="Basic HTML is supported and sanitized on save.">
          <textarea
            className="input font-mono text-sm"
            rows={8}
            value={form.body}
            onChange={(e) => update('body', e.target.value)}
            placeholder="What happened at this event?"
          />
        </Field>

        <Field label="Photos" hint="Images are attached in the order shown. Upload one or more; reorder or remove below.">
          <input type="file" accept="image/*" multiple onChange={handleUpload} className="text-sm" />
          {uploading && <p className="mt-1 text-xs text-ink-muted">Uploading…</p>}
        </Field>

        {form.media.length > 0 && (
          <ul className="grid gap-3 sm:grid-cols-2">
            {form.media.map((m, i) => (
              <li
                key={m.id}
                className="flex items-center gap-3 rounded-xl border border-cream-300 bg-surface-alt p-2"
              >
                <img
                  src={assetUrl(m.url)}
                  alt={m.alt_text || ''}
                  className="h-14 w-14 shrink-0 rounded-lg object-cover"
                />
                <span className="min-w-0 flex-1 truncate text-sm text-ink-soft">
                  {m.alt_text || `Photo ${i + 1}`}
                </span>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => movePhoto(i, -1)}
                    disabled={i === 0}
                    aria-label="Move photo up"
                    className="btn-ghost px-2 py-1 text-xs disabled:opacity-40"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => movePhoto(i, 1)}
                    disabled={i === form.media.length - 1}
                    aria-label="Move photo down"
                    className="btn-ghost px-2 py-1 text-xs disabled:opacity-40"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() => removePhoto(m.id)}
                    aria-label="Remove photo"
                    className="btn-ghost px-2 py-1 text-xs text-red-600"
                  >
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap items-center gap-3 border-t border-cream-300 pt-5">
          <button onClick={handlePublish} className="btn-primary" disabled={busy || uploading}>
            {busy ? 'Working…' : 'Publish'}
          </button>
          <button onClick={handleSaveDraft} className="btn-outline" disabled={busy || uploading}>
            Save draft
          </button>
          {form.id && (
            <button onClick={resetForm} className="btn-ghost ml-auto" disabled={busy}>
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Recaps list */}
      {state.loading ? (
        <PageLoader />
      ) : state.error ? (
        <ErrorState error={state.error} onRetry={loadRecaps} />
      ) : state.items.length === 0 ? (
        <EmptyState title="No recaps yet" message="Create the first recap for this event above." />
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-cream-300 bg-surface-alt text-xs uppercase tracking-wider text-ink-soft">
              <tr>
                <th className="px-5 py-3">Title</th>
                <th className="px-5 py-3">Photos</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Published</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream-300">
              {state.items.map((r) => (
                <tr key={r.id} className="hover:bg-surface-alt">
                  <td className="px-5 py-3 font-medium text-ink">{r.title || <span className="text-ink-muted">Untitled recap</span>}</td>
                  <td className="px-5 py-3 text-ink-muted">{r.media?.length || 0}</td>
                  <td className="px-5 py-3">
                    <StatusBadge status={r.status} />
                  </td>
                  <td className="px-5 py-3 text-ink-muted">
                    {r.published_at ? formatDate(r.published_at) : '—'}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap justify-end gap-2">
                      <button onClick={() => editRecap(r)} className="btn-outline px-3 py-1.5 text-xs">
                        Edit
                      </button>
                      {r.status !== 'published' && (
                        <button
                          onClick={() => handlePublishRow(r)}
                          className="btn-primary px-3 py-1.5 text-xs"
                          disabled={busy}
                        >
                          Publish
                        </button>
                      )}
                      <button
                        onClick={() => setConfirmDelete(r)}
                        className="btn-ghost px-3 py-1.5 text-xs text-red-600"
                        disabled={busy}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-maroon-950/60 p-4"
          onClick={() => setConfirmDelete(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="w-full max-w-md rounded-2xl bg-surface-card p-6 shadow-lift"
            onClick={(ev) => ev.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-ink">Delete recap?</h3>
            <p className="mt-2 text-sm text-ink-muted">
              This recap will be removed. Attached photos remain in the media library. This cannot be undone.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setConfirmDelete(null)} className="btn-ghost" disabled={busy}>
                Cancel
              </button>
              <button onClick={() => handleDelete(confirmDelete)} className="btn-danger" disabled={busy}>
                {busy ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
